import { useState, useCallback, useEffect } from "react";
import {
  DateInput,
  Button,
  OptionGroup,
  DataTable,
  InputField,
} from "@/components";
import { RightWrapper } from "../../../components";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import ColumnHelper from "@/utils/ColumnHelper";
import { useMemo } from "react";
import { useFilteredData, useApi } from "@/";
import { getCreditNoteData } from "../../../utils/Data";
import { HiOutlineDownload } from "react-icons/hi";
import { BsFiletypeXls } from "react-icons/bs";
import { RiFileExcel2Line } from "react-icons/ri";
import CreditNoteForm from "./CreditNoteForm";
import { useDispatch, useSelector } from "react-redux";
import { fetchCreditNotes } from "../../../app/Slices";
import CreditNoteBulk from "./CreditNoteBulk";
import { subDays } from "date-fns";
const CreditNote = () => {
  const dateRange =
    useSelector((state) => state.dashboard?.data?.dateRange) || {};
  const currentDate = new Date();
  const startOfMonthDate = dateRange?.startDate
    ? new Date(dateRange.startDate)
    : subDays(currentDate, 30); // Last 30 days
  const endOfMonthDate = dateRange?.endDate
    ? new Date(dateRange.endDate)
    : currentDate;
  const options = [
    { label: "Bulk CN", value: "bulk_cn", icon: <BsFiletypeXls /> },
  ];
  const [sheetOpen, setSheetOpen] = useState(false);
  const [filters, setFilters] = useState({
    date: { from: startOfMonthDate, to: endOfMonthDate },
    customerName: "",
    customerId: "",
  });
  const dispatch = useDispatch();
  const fetchCreditData = async () => {
    await dispatch(fetchCreditNotes());
  };
  useEffect(() => {
    fetchCreditData();
  }, [dispatch]);
  const creditNoteData =
    useSelector((state) => state.creditNote.creditNotes) || [];
  const creditNoteIsLoading = useSelector((state) => state.creditNote.loading);
  const handleOptionClick = (value) => {
    console.log("Option Selected:", value);
    if (value === "bulk_cn") {
      handleDownload(selectedRows.map((row) => row._id));
    }
  };
  const [selectedRows, setSelectedRows] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [resetPageCounter, setResetPageCounter] = useState(0);
  const { callApi } = useApi();
  // Ensure rawData is only generated once
  const rawData = useMemo(() => getCreditNoteData(10), []);

  // Filter configuration
  const filterConfig = useMemo(
    () => ({
      date: { type: "date", itemKey: "creditNoteDate" },
      customerName: { type: "partical", itemKey: "userId.name" },
      customerId: { type: "partical", itemKey: "userId.clientId" },
    }),
    []
  );

  // Memoize filtered data to prevent unnecessary re-renders
  const { data: finalFilteredData, isLoading } = useFilteredData(
    creditNoteData,
    appliedFilters,
    filterConfig
  );
  const isCreditNoteLoading = isLoading || creditNoteIsLoading;
  const handleDownload = async (ids) => {
    const loadingIndicator = document.createElement("div");
    loadingIndicator.textContent = "Downloading Credit Note:...";
    loadingIndicator.style.position = "fixed";
    loadingIndicator.style.bottom = "20px";
    loadingIndicator.style.right = "20px";
    loadingIndicator.style.backgroundColor = "#4CAF50";
    loadingIndicator.style.color = "white";
    loadingIndicator.style.padding = "10px";
    loadingIndicator.style.borderRadius = "5px";
    loadingIndicator.style.zIndex = "1000";
    document.body.appendChild(loadingIndicator);
    try {
      console.log("Downloading Credit Note:", ids);

      // Add loading indicator

      // Use the existing callApi hook with blob response type
      const response = await callApi(
        "/admin/downloadCreditNote",
        "POST",
        { ids },
        null,
        {
          Accept: "application/pdf",
        },
        "blob"
      );

      // Check if response is valid
      if (!response || response.size === 0) {
        console.error("Empty response received");
        document.body.removeChild(loadingIndicator);
        return;
      }

      // Create blob with proper MIME type
      const blob = new Blob([response], { type: "application/pdf" });

      // Create object URL
      const url = window.URL.createObjectURL(blob);

      // Create and trigger download
      const a = document.createElement("a");
      a.href = url;
      a.download = `CreditNote-${new Date().getTime()}.pdf`;
      document.body.appendChild(a);
      a.click();

      // Cleanup with appropriate timeout
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        document.body.removeChild(loadingIndicator);
      }, 2000);
    } catch (error) {
      console.error("Export error:", error);
      alert("Failed to download Credit Note. Please try again.");
      document.body.removeChild(loadingIndicator);
    }
  };
  // Format currency values
  const formatCurrency = useCallback((value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(value);
  }, []);

  // Format date values
  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);
  const formatLongDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: undefined,
    });
  }, []);
  const formatCreditNoteNumber = useCallback((number) => {
    if (!number) return 'N/A';
    // Assuming the input format is CN25050001
    const matches = number.match(/CN(\d{2})(\d{2})(\d{4})/);
    if (!matches) return number;
    
    return `ICC/CN/${matches[1]}/${matches[2]}/${matches[3]}`;
  }, []);

  const columns = useMemo(
    () => [
      ColumnHelper({
        header: "Customer Name",
        accessorKey: "",
        cell: (info) => info.row.original.userId?.name || "N/A",
        meta: {
          tooltip: (row) => row.userId?.name || "N/A",
        },
      }),
      ColumnHelper({
        header: "Customer ID",
        accessorKey: "",
        cell: (info) => info.row.original.userId?.clientId || "N/A",
        meta: {
          tooltip: (row) => row.userId?.clientId || "N/A",
        },
      }),
      ColumnHelper({
        header: "Credit ID",
        accessorKey: "creditNoteNumber",
        cell: (info) => formatCreditNoteNumber(info.row.original.creditNoteNumber),
        meta: {
          tooltip: (row) => formatCreditNoteNumber(row.creditNoteNumber),
        },
      }),
      ColumnHelper({
        header: "Credit Date",
        accessorKey: "creditNoteDate",
        cell: (info) => formatDate(info.row.original.creditNoteDate),
        meta: {
          tooltip: (row) => formatDate(row.creditNoteDate),
        },
      }),
      ColumnHelper({
        header: "Credit Period",
        accessorKey: "",
        cell: (info) => formatLongDate(info.row.original.creditNoteDate),
        meta: {
          tooltip: (row) => formatLongDate(row.creditNoteDate),
        },
      }),
      ColumnHelper({
        header: "Credit Amount",
        accessorKey: "totalAmount",
        cell: (info) => (
          <span className="font-nunito">
            {formatCurrency(info.row.original.totalAmount)}
          </span>
        ),
        meta: {
          tooltip: (row) => (
            <span className="font-nunito">
              {formatCurrency(row.totalAmount)}
            </span>
          ),
        },
      }),
      ColumnHelper({
        header: "Download",
        accessorKey: "_id",
        cell: (info) => (
          <div className="flex gap-1 justify-start items-center">
            <Button
              size="xs"
              icon={HiOutlineDownload}
              onClick={() => handleDownload([info.row.original._id])}
            />
          </div>
        ),
        meta: {
          tooltip: () => "Download credit details",
        },
      }),
    ],
    []
  );
  const handleBulk = () => {
    setSheetOpen(true); // Opens the sheet for bulk updates
  };

  // Handle filter changes
  const handleChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleDateChange = useCallback((dateRange) => {
    setFilters((prev) => ({
      ...prev,
      date: { from: dateRange?.from || "", to: dateRange?.to || "" },
    }));
  }, []);

  // Apply filters without resetting raw data
  const handleApply = useCallback(() => {
    setAppliedFilters(filters);
    setResetPageCounter((prev) => prev + 1);
  }, [filters]);

  // Reset filters properly
  const handleReset = useCallback(() => {
    const initialFilters = {
      date: { from: startOfMonthDate, to: endOfMonthDate },
      customerName: "",
      customerId: "",
    };
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setResetPageCounter((prev) => prev + 1);
  }, []);
  return (
    <RightWrapper>
      <div className="flex flex-col gap-4 mt-2 px-2 relative pb-20 md:pb-4">
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetContent aria-describedby="">
            <SheetHeader>
              <SheetTitle className="font-nunito text-headline5 md:text-headline4 text-secondary/90 font-bold">
                Create Credit Note
              </SheetTitle>
            </SheetHeader>
            <CreditNoteBulk setSheetOpen={setSheetOpen} />
          </SheetContent>
        </Sheet>
        {/* Filters Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <DateInput
            label="Date Filter"
            placeholder="Select From & To date"
            mode="range"
            value={filters.date}
            onChange={(value) => handleChange("date", value)}
          />
          <InputField
            label="Customer ID"
            placeholder="Enter Customer ID"
            value={filters.customerId}
            onChange={(val) => handleChange("customerId", val || "")}
          />
          <InputField
            label="Customer Name"
            placeholder="Enter Customer Name"
            value={filters.customerName}
            onChange={(val) => handleChange("customerName", val || "")}
          />
          <div className="flex gap-2 justify-end sm:justify-start">
            <Button variant="default" size="md" onClick={handleApply}>
              Apply
            </Button>
            <Button variant="outline" size="md" onClick={handleReset}>
              Reset
            </Button>
          </div>
        </div>

        {/* Option Group Section */}
        {selectedRows.length > 0 && (
          <OptionGroup
            options={options}
            selectedCount={selectedRows.length}
            onClick={handleOptionClick}
          />
        )}
        <DataTable
          columns={columns}
          tittle="Credit Note"
          data={finalFilteredData}
          getSelectedRows={setSelectedRows}
          isLoading={isCreditNoteLoading}
          resetPage={resetPageCounter}
          actions={[
            {
              name: "Upload CN",
              onClick: handleBulk,
              icon: BsFiletypeXls,
            },
            {
              name: "Export All",
              onClick: () => {},
              icon: RiFileExcel2Line,
              body: {
                model: "Admin: Export Credit Note",
                filename: `Credit_Note_${new Date().getTime()}`,
              },
            },
          ]}
        />
      </div>
    </RightWrapper>
  );
};

export default CreditNote;
